import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, Building2, Phone, Mail, IndianRupee, Receipt } from "lucide-react";

const VENDOR_TYPES = [
  { value: "omc",        label: "OMC" },
  { value: "petrolpump", label: "Petrol Pump" },
  { value: "private",    label: "Private Supplier" },
];
const PAY_METHODS = ["bank", "upi", "cash", "cheque", "neft", "rtgs"];

const empty = {
  name: "", vendor_type: "petrolpump",
  gstin: "", pan: "", contact_person: "", phone: "", email: "",
  address: "", city: "", state: "", pincode: "",
  notes: "", active: true,
};
const emptyPay = { po_id: "", amount: 0, payment_date: new Date().toISOString().slice(0, 10), method: "bank", reference_no: "", notes: "" };

function money(n) { return `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`; }
function typeLabel(t) { return (VENDOR_TYPES.find((x) => x.value === t) || {}).label || t; }
function typePill(t) {
  const cls = {
    omc: "bg-blue-100 text-blue-700 border-blue-300",
    petrolpump: "bg-amber-100 text-amber-800 border-amber-300",
    private: "bg-slate-100 text-slate-700 border-slate-300",
  }[t] || "bg-slate-100 text-slate-500 border-slate-300";
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${cls}`}>{typeLabel(t)}</span>;
}

export default function Vendors() {
  const qc = useQueryClient();
  const [typeFilter, setTypeFilter] = useState("all");
  const { data } = useQuery({
    queryKey: ["vendors", typeFilter],
    queryFn: () => api.get(
      `/vendors?active_only=false&with_finance=true${typeFilter !== "all" ? `&vendor_type=${typeFilter}` : ""}`,
    ).then((r) => r.data),
  });

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);

  // Vendor statement drawer
  const [stmtVendor, setStmtVendor] = useState(null);
  const { data: statement } = useQuery({
    queryKey: ["vendor-statement", stmtVendor?.id],
    queryFn: () => api.get(`/vendors/${stmtVendor.id}/statement`).then((r) => r.data),
    enabled: !!stmtVendor,
  });

  // Record payment dialog
  const [payVendor, setPayVendor] = useState(null);
  const [payForm, setPayForm] = useState(emptyPay);
  const { data: unpaidPos } = useQuery({
    queryKey: ["unpaid-pos", payVendor?.name],
    queryFn: () => api.get(`/procurement/purchase-orders`).then((r) => r.data),
    enabled: !!payVendor,
    select: (d) => (d.items || []).filter((p) => p.vendor === payVendor?.name && ["received", "released"].includes(p.status)),
  });
  const recordPay = useMutation({
    mutationFn: (payload) => api.post(`/vendors/${payVendor.id}/payments`, payload),
    onSuccess: () => {
      toast.success("Payment recorded");
      setPayVendor(null);
      setPayForm(emptyPay);
      qc.invalidateQueries({ queryKey: ["vendors"] });
      qc.invalidateQueries({ queryKey: ["vendor-statement"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to record payment"),
  });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ ...empty, ...v }); setOpen(true); };

  const save = useMutation({
    mutationFn: (payload) => editing
      ? api.patch(`/vendors/${editing.id}`, payload)
      : api.post("/vendors", payload),
    onSuccess: () => {
      toast.success(editing ? "Vendor updated" : "Vendor created");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["vendors"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/vendors/${id}`),
    onSuccess: (r) => {
      if (r.data?.deactivated) toast.info(`Vendor deactivated (${r.data.referenced_by_pos} historical POs kept)`);
      else toast.success("Vendor deleted");
      qc.invalidateQueries({ queryKey: ["vendors"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });

  const rows = useMemo(() => data?.items || [], [data]);
  const counts = useMemo(() => {
    const c = { omc: 0, petrolpump: 0, private: 0 };
    for (const v of rows) if (v.active !== false) c[v.vendor_type] = (c[v.vendor_type] || 0) + 1;
    return c;
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="vendors-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Vendor Master</h1>
          <p className="text-sm text-muted-foreground">
            Fuel suppliers — OMCs ({counts.omc}), petrol pumps ({counts.petrolpump}), private ({counts.private}). Used by Procurement.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-40" data-testid="vendor-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {VENDOR_TYPES.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="btn-new-vendor"><Plus size={14} className="mr-1" /> New Vendor</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg" data-testid="vendor-dialog">
              <DialogHeader>
                <DialogTitle>{editing ? `Edit ${editing.name}` : "New vendor"}</DialogTitle>
                <DialogDescription>
                  {editing ? "Update supplier details." : "Add a new fuel supplier to the master list."}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label>Vendor name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. HP Pune Chinchwad" data-testid="vendor-name" />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.vendor_type} onValueChange={(v) => setForm({ ...form, vendor_type: v })}>
                    <SelectTrigger data-testid="vendor-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VENDOR_TYPES.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border/60 p-2 sm:mt-6">
                  <input
                    type="checkbox"
                    checked={form.active !== false}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="h-4 w-4 shrink-0"
                    data-testid="vendor-active"
                  />
                  <span className="text-sm">Active <span className="text-muted-foreground">(available for new POs)</span></span>
                </label>
                <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="27ABCDE1234F1Z5" /></div>
                <div><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" /></div>
                <div><Label>Contact person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} placeholder="Manager name" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 90000 00000" /></div>
                <div className="sm:col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ops@vendor.in" /></div>
                <div className="sm:col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Payment terms, credit period, etc." /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={() => save.mutate(form)} disabled={!form.name.trim() || save.isPending} data-testid="btn-save-vendor">
                  {save.isPending ? "Saving…" : editing ? "Save changes" : "Create vendor"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Vendor</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3 text-right">Purchased</th>
                  <th className="px-4 py-3 text-right">Paid</th>
                  <th className="px-4 py-3 text-right">Outstanding</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => {
                  const fin = v.finance || {};
                  const outstanding = fin.outstanding || 0;
                  return (
                    <tr key={v.id} className="border-b border-border/40 text-sm" data-testid={`vendor-row-${v.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{v.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                          {v.gstin || v.contact_person || v.city || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3">{typePill(v.vendor_type)}</td>
                      <td className="px-4 py-3 text-xs">
                        {v.phone && <div className="flex items-center gap-1"><Phone size={10} /> {v.phone}</div>}
                        {v.email && <div className="flex items-center gap-1"><Mail size={10} /> {v.email}</div>}
                        {!v.phone && !v.email && <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{money(fin.total_purchased)}</td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-700">{money(fin.total_paid)}</td>
                      <td className={`px-4 py-3 text-right font-mono font-bold ${outstanding > 0 ? "text-red-600" : "text-slate-500"}`} data-testid={`vendor-outstanding-${v.id}`}>
                        {money(outstanding)}
                      </td>
                      <td className="px-4 py-3">
                        {v.active !== false
                          ? <span className="inline-flex items-center rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-700">Active</span>
                          : <span className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">Inactive</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => setStmtVendor(v)} data-testid={`btn-statement-${v.id}`}>
                            <Receipt size={12} className="mr-1" /> Statement
                          </Button>
                          <Button size="sm" onClick={() => { setPayVendor(v); setPayForm({ ...emptyPay, amount: outstanding > 0 ? outstanding : 0 }); }} data-testid={`btn-pay-${v.id}`}>
                            <IndianRupee size={12} className="mr-1" /> Pay
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => openEdit(v)} data-testid={`btn-edit-vendor-${v.id}`}>
                            <Edit2 size={14} />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              if (window.confirm(`Delete ${v.name}? (Kept as inactive if referenced by past POs.)`)) del.mutate(v.id);
                            }}
                            data-testid={`btn-delete-vendor-${v.id}`}
                          >
                            <Trash2 size={14} className="text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-sm text-muted-foreground">
                      <Building2 size={26} className="mx-auto mb-2 opacity-40" />
                      No vendors yet. Click <b>New Vendor</b> to add your first supplier.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ────────── Statement Dialog ────────── */}
      <Dialog open={!!stmtVendor} onOpenChange={(v) => !v && setStmtVendor(null)}>
        <DialogContent className="sm:max-w-3xl" data-testid="statement-dialog">
          <DialogHeader>
            <DialogTitle>Ledger · {stmtVendor?.name}</DialogTitle>
            <DialogDescription>
              Chronological POs (debit) + payments (credit) with running balance.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 rounded-lg border border-border/60 p-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Purchased</div>
              <div className="font-mono text-lg font-bold">{money(statement?.total_purchased)}</div>
              <div className="text-[10px] text-muted-foreground">{statement?.po_count || 0} POs</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Paid</div>
              <div className="font-mono text-lg font-bold text-emerald-700">{money(statement?.total_paid)}</div>
              <div className="text-[10px] text-muted-foreground">{statement?.payment_count || 0} payments</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Outstanding</div>
              <div className={`font-mono text-lg font-bold ${(statement?.outstanding || 0) > 0 ? "text-red-600" : "text-slate-500"}`}>{money(statement?.outstanding)}</div>
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 border-b border-border/60 bg-muted/60 text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Ref</th>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2 text-right">Debit</th>
                  <th className="px-3 py-2 text-right">Credit</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {(statement?.entries || []).map((e, i) => (
                  <tr key={i} className="border-b border-border/40">
                    <td className="px-3 py-2 whitespace-nowrap text-xs">{(e.date || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{e.ref}</td>
                    <td className="px-3 py-2 text-xs">{e.description}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{e.debit ? money(e.debit) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-emerald-700">{e.credit ? money(e.credit) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs font-bold">{money(e.balance)}</td>
                  </tr>
                ))}
                {(statement?.entries || []).length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-xs text-muted-foreground">No transactions yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* ────────── Record Payment Dialog ────────── */}
      <Dialog open={!!payVendor} onOpenChange={(v) => !v && setPayVendor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle>Record payment · {payVendor?.name}</DialogTitle>
            <DialogDescription>Reduces vendor outstanding. Reflected in accounts BI immediately.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Against PO (optional)</Label>
              <Select value={payForm.po_id || "__on_account__"} onValueChange={(v) => setPayForm({ ...payForm, po_id: v === "__on_account__" ? "" : v })}>
                <SelectTrigger data-testid="pay-po"><SelectValue placeholder="On-account payment" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__on_account__">— On-account (no specific PO) —</SelectItem>
                  {(unpaidPos || []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.code} · {money(p.total_amount)} · {p.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (₹)</Label>
              <Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: Number(e.target.value) })} data-testid="pay-amount" />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} />
            </div>
            <div>
              <Label>Method</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_METHODS.map((m) => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference No</Label>
              <Input value={payForm.reference_no} onChange={(e) => setPayForm({ ...payForm, reference_no: e.target.value })} placeholder="UTR / Cheque #" />
            </div>
            <div className="sm:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayVendor(null)}>Cancel</Button>
            <Button
              onClick={() => recordPay.mutate(payForm)}
              disabled={!payForm.amount || payForm.amount <= 0 || recordPay.isPending}
              data-testid="btn-save-payment"
            >
              {recordPay.isPending ? "Saving…" : `Record ${money(payForm.amount)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
